export type BeatLineDrawCardContext = {
  state: Record<string, any>;
  beatlineUi?: Record<string, any>;
  beatlineGetPlayerCountEffective?: () => number;
  beatlineShouldUseCompactLayout?: (playerCount: number) => boolean;
  beatlineHeaderScrollToTurn?: (slot: number) => void;
  updateUI?: (skipTimelineRebuild?: boolean) => void;
  beatlineHeardMarkSeen?: (id: unknown) => void;
  beatlineQrSessionPushTrack?: (spotifyId: string, youtubeId: string, itunesId: string) => void;
  createCardHTML?: (card: Record<string, any>, revealed: boolean, opts?: Record<string, any>) => string;
  playMusic?: (id: unknown) => void;
  beatlineSetDragEnabled?: (enabled: boolean) => void;
  beatlineScheduleDragNudge?: (id: unknown) => void;
};

export type BeatLineDrawCardResult = {
  handled: boolean;
  reason?: string;
};

function safeCall(fn: unknown, ...args: any[]): void {
  if (typeof fn !== 'function') return;
  try { fn(...args); } catch (_) {}
}

function setDisplay(id: string, display: string): HTMLElement | null {
  const el = document.getElementById(id);
  if (el) el.style.display = display;
  return el;
}

function animateCheckButton(checkBtn: HTMLElement | null): void {
  if (!checkBtn || typeof checkBtn.animate !== 'function') return;
  try {
    checkBtn.animate([
      { transform: 'scale(0.9)', opacity: 0 },
      { transform: 'scale(1)', opacity: 1 },
    ], { duration: 300, easing: 'ease-out' });
  } catch (_) {}
}

function focusActiveTimeline(ctx: BeatLineDrawCardContext): void {
  const state = ctx.state;
  const playerCount = typeof ctx.beatlineGetPlayerCountEffective === 'function'
    ? ctx.beatlineGetPlayerCountEffective()
    : parseInt(String(state.playerCount || 1), 10) || 1;
  const compact = typeof ctx.beatlineShouldUseCompactLayout === 'function'
    ? !!ctx.beatlineShouldUseCompactLayout(playerCount)
    : false;
  const turnSlot = parseInt(String(state.turn || 1), 10) || 1;
  const ui = ctx.beatlineUi;

  if (compact && ui && ui.focusSlot !== turnSlot) {
    ui.focusSlot = turnSlot;
    ui.userSetFocus = false;
    safeCall(ctx.updateUI, false);
    safeCall(ctx.beatlineHeaderScrollToTurn, turnSlot);
  }
}

/**
 * C6: TypeScript-owned happy-path drawCard.
 *
 * It intentionally handles the normal local/preview flow only. Complex branches
 * such as deck reset, solo end states and online overrides fall back to the
 * original legacy drawCard, so the golden-master behaviour remains intact.
 */

function beatlineBlockSurvivalDrawUntilNewCardClick(ctx: any): boolean {
  // beatline_survival_drawcard_gate_v9
  try {
    const state = ctx && ctx.state ? ctx.state : null;
    if (!state || !state.survival || state.survivalEnded) return false;
    if (!state.survivalAwaitingNewCard) return false;

    const w = window as any;

    if (w.__beatlineSurvivalAllowNextDraw === true) {
      w.__beatlineSurvivalAllowNextDraw = false;
      state.survivalAwaitingNewCard = false;
      state.survivalNewCardReadyAt = 0;
      return false;
    }

    try {
      if (ctx && typeof ctx.stopMusic === 'function') ctx.stopMusic();
    } catch (_) {}

    const btnNext = document.getElementById('btn-next') as HTMLButtonElement | null;
    const btnCheck = document.getElementById('btn-check') as HTMLButtonElement | null;
    const deckZone = document.getElementById('deck-zone') as HTMLElement | null;

    if (deckZone && !state.activeCard) {
      deckZone.innerHTML = '';
    }

    if (btnCheck) {
      btnCheck.style.display = 'none';
      btnCheck.disabled = false;
      btnCheck.style.pointerEvents = 'auto';
    }

    if (btnNext) {
      let label = 'NIEUWE KAART';

      try {
        if (ctx && typeof ctx.t === 'function') {
          label = String(ctx.t('btn_new_card') || label);
        }
      } catch (_) {}

      btnNext.textContent = label;
      btnNext.style.display = 'flex';
      btnNext.disabled = false;
      btnNext.style.pointerEvents = 'auto';
    }

    return true;
  } catch (_) {
    return false;
  }
}

export async function drawCardFromTypeScript(ctx: BeatLineDrawCardContext): Promise<BeatLineDrawCardResult> {
  if (beatlineBlockSurvivalDrawUntilNewCardClick(ctx)) {
    return { handled: true };
  }

  const state = ctx.state;
  if (!state) return { handled: false, reason: 'missing-state' };

  if (state.perfectOrder) return { handled: true, reason: 'perfect-order-skip' };
  if (state.gameEnded) return { handled: true, reason: 'game-ended' };

  // Keep these more complex modes in the golden legacy code until they are mapped separately.
  if (state.survival || state.timeAttack || state.speedrun) {
    return { handled: false, reason: 'complex-mode' };
  }

  if (!Array.isArray(state.deck) || state.deck.length === 0) {
    return { handled: false, reason: 'deck-empty' };
  }

  focusActiveTimeline(ctx);

  const nextCard = state.deck.pop();
  if (!nextCard) return { handled: false, reason: 'no-card-popped' };

  state.activeCard = nextCard;
  if (state.activeCard && state.activeCard.id) safeCall(ctx.beatlineHeardMarkSeen, state.activeCard.id);

  try {
    if (state.qrMode && state.activeCard && state.activeCard.id) {
      const spotifyId = state.activeCard.id_spotify || state.activeCard.id || '';
      const youtubeId = state.activeCard.id_youtube || '';
      const itunesId = state.activeCard.id_itunes || '';
      safeCall(ctx.beatlineQrSessionPushTrack, String(spotifyId), String(youtubeId), String(itunesId));
    }
  } catch (_) {}

  state.cardDrawnAt = Date.now();

  setDisplay('btn-next', 'none');
  const checkBtn = setDisplay('btn-check', 'flex') as HTMLButtonElement | null;
  animateCheckButton(checkBtn);

  const deckZone = document.getElementById('deck-zone');
  if (deckZone) {
    if (typeof ctx.createCardHTML === 'function') {
      deckZone.innerHTML = ctx.createCardHTML(state.activeCard, false);
    } else {
      deckZone.innerHTML = '';
    }
  }

  safeCall(ctx.playMusic, state.activeCard.id);
  safeCall(ctx.beatlineSetDragEnabled, true);

  if (checkBtn) checkBtn.disabled = false;
  safeCall(ctx.beatlineScheduleDragNudge, state.activeCard && state.activeCard.id);

  return { handled: true, reason: 'ts-happy-path' };
}
